import angular from 'angular';
import APIV5 from '~/models/api';
import Form from '../../scripts/services/model/form';
import State from '../../scripts/services/model/state';
import Workflow from '../../scripts/services/model/workflow';

export default class WorkflowController {
	#$injector;

	#$location

	#$routeParams;

	#$scope;

	#$timeout;

	#$window;

	#onWindowResize;

	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {any} $scope
	 */
	constructor($injector, $scope) {
		this.#$injector = $injector;
		this.#$location = $injector.get('$location');
		this.#$routeParams = $injector.get('$routeParams');
		this.#$scope = $scope;
		this.#$timeout = $injector.get('$timeout');
		this.#$window = $injector.get('$window');
		this.FormService = $injector.get('FormService');
		this.User = $injector.get('User');

		this.actionKid = undefined;
		this.affectKid = undefined;
		this.disabled = false;
		this.file = undefined;
		this.form = undefined;

		/** @type {import('~/components/wf/wf-form-actions/wf-form-actions.controller').WfFormActionsControllerOptions} */
		this.formActionsOptions = {};

		this.loading = false;
		this.options = {
			readOnly: true,
			rg:       true,
		};
		this.resizing = false;
		this.submission = undefined;
		this.pendingWf = false;
		this.profil = undefined;
		this.wf = undefined;
		this.width = this.#$window.innerWidth;

		this.#onWindowResize = () => {
			this.width = this.#$window.innerWidth;
			this.initWidth();
			this.#$scope.$digest();
		};
		angular.element(this.#$window).on('resize', this.#onWindowResize);

		// Le contrôleur est recréé à chaque navigation vers /workflow/:affectKid : sans ce
		// désabonnement, chaque workflow visité laisse un listener resize sur window qui
		// retient le contrôleur, son scope et le formulaire chargé.
		this.#$scope.$on('$destroy', () => {
			angular.element(this.#$window).off('resize', this.#onWindowResize);
		});

		// Le même contrôleur sert de page (route /workflow/:affectKid) et de contenu
		// de modale. Les deux contextes ne se détectent pas par injection :
		// $uibModalInstance et les `resolve` sont des *locals* passés à $controller()
		// par ui-bootstrap, invisibles pour $injector.get() — et absents en page, où
		// les demander ferait échouer l'instanciation. ui-bootstrap pose en revanche
		// $close / $dismiss / $resolve sur le scope : c'est le seul canal commun.
		this.isModal = angular.isFunction(this.#$scope.$close);

		const resolved = this.#$scope.$resolve ?? {};
		const affectKid = resolved.affectKid ?? this.#$routeParams.affectKid;

		if (this.isModal) {
			// $viewContentLoaded n'est émis que par ngView : en modale, il faut lancer
			// le chargement nous-mêmes.
			(async () => this.init(affectKid))();
		}
		else {
			this.#$scope.$on('$viewContentLoaded', () => this.init(affectKid));
		}

		// functionnality for popin workflow reload
		// $rootScope.$on('wfReload', () => {
		// 	this.init();
		// });

		let search;
		try {
			search = JSON.parse(this.#$location.search().params);
		}
		catch {
			search = undefined;
		}

		this.formActionsOptions.hideHistory = false;
		this.formActionsOptions.from = {
			path: this.#$location.search().from,
			search,
		};

		this.initWidth();
	}

	/**
	 * Sortie de l'écran, quel que soit le contexte : fermeture de la modale, ou
	 * retour à l'écran d'origine.
	 * @param {{type: string}} result Résultat transmis à l'ouvreur de la modale
	 */
	#leave(result) {
		if (this.isModal) {
			// Fermeture et non rejet : l'appelant fait `await modal.open(...)`, un
			// $dismiss lèverait chez lui une erreur à traiter.
			this.#$scope.$close(result);
			return;
		}

		if (this.formActionsOptions.from?.path) {
			this.#$location
				.path(this.formActionsOptions.from.path)
				.search(this.formActionsOptions.from.search);
		}
		else {
			this.#$location.url('/');
		}
	}

	cancel() {
		this.#leave({ type: 'cancel' });
	}

	/**
	 * Workflow traité. Même sortie que `cancel()`, mais le résultat permet au
	 * ktable appelant de ne se rafraîchir que sur un succès.
	 *
	 * En page sans écran d'origine, on reste sur place : c'est le comportement
	 * actuel, le template ne câblait alors aucune redirection.
	 */
	done() {
		if (this.isModal || this.formActionsOptions.from?.path) {
			this.#leave({ type: 'success' });
		}
	}

	// eslint-disable-next-line class-methods-use-this
	/**
	 * @param {Form} form
	 * @returns {string | undefined} form name
	 */
	getName(form) {
		let name = form.isa.objname;
		if (form.name) {
			name += ` : ${form.name}`;
		}
		return name;
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').KNode} affectKid
	 * @returns {Promise<void>}
	 */
	async init(affectKid) {
		this.affectKid = affectKid;
		try {
			const wfAffect = await APIV5.instance.wfAffectInfo({ wfaffect_kid: this.affectKid }, { id: 'workflow.init' });
			this.wf = {
				allocatorTo: wfAffect.allocator_to,
				comment:     wfAffect.wfcomment,
				haveAction:  wfAffect.haveAction,
				profilFrom:  wfAffect.profil_from,
				state:       Object.assign(new State(this.#$injector, wfAffect.wfstate.kid), wfAffect.wfstate),
				status:      wfAffect.wfstatus,
				workflow:    Object.assign(new Workflow(this.#$injector, wfAffect.wf.kid), wfAffect.wf),
			};
			this.actionKid = wfAffect.wfaction.kid;
			this.disabled = this.wf.status.kid !== 'K_NODE;WFS_INPROGRESS';
			const documentKid = wfAffect.document.kid;

			const result = await this.FormService.load(documentKid, this.options);
			this.form = result.form;
			this.form.id = `KF${this.form.kid.split(';')[1]}`;
			this.selectedForm = this.form.kid;
			this.submission = result.submission;
			this.form.droit = this.submission.kdroit;
			this.form.tab = this.form.isfile ? 'file' : 'form';

			if (!this.form.isTerminal) {
				this.name = this.getName(this.form);
			}

			this.#$timeout(() => {
				this.tab = this.form.tab;
				if (this.tab === 'file') {
					this.#$timeout(() => {
						this.refreshTabFile();
						this.#$scope.$apply();
					});
				}

				this.#$scope.$apply();
			}, 100);
		}
		catch (error) {
			console.error(error);
			this.loading = false;
			this.wf = undefined;
		}

		this.#$scope.$on('angular-resizable.resizing', () => {
			if (!this.resizing) {
				this.resizing = true;
				this.#$scope.$apply();
			}
		});

		this.#$scope.$on('angular-resizable.resizeEnd', () => {
			if (this.resizing) {
				this.#$timeout(() => {
					this.resizing = false;
				}, 50);
			}
		});
	}

	initWidth() {
		this.leftW = (this.width / 3) - 10;
		this.rightW = this.leftW;
		this.centerW = this.rightW;
		this.leftS = true;
		this.rightS = true;
		this.centerS = true;
	}

	refreshTabFile() {
		this.tab = 'file';
		if (this.form.viewer_engine === 'pdf') {
			this.#$scope.$broadcast('Form.change-tab');
		}
	}

	refreshWorkflows() {
		this.#$scope.$broadcast('wf-list:refresh');
	}

	resizeLeft() {
		this.centerW = 0;
		this.leftW = this.width - 30;
		this.rightW = 0;
		this.rightS = false;
		this.centerS = false;
		this.leftS = true;
	}

	resizeCenter() {
		this.leftW = 0;
		this.rightW = 0;
		this.centerW = this.width - 30;
		this.centerS = true;
		this.leftS = false;
		this.rightS = false;
	}

	resizeRight() {
		this.leftW = 0;
		this.rightW = this.width - 30;
		this.centerW = 0;
		this.rightS = true;
		this.leftS = false;
		this.centerS = false;
	}

	restore() {
		this.leftW = (this.width / 3) - 10;
		this.rightW = (this.width / 3) - 10;
		this.centerW = (this.width / 3) - 10;
		this.rightS = true;
		this.leftS = true;
		this.centerS = true;
	}
}
